import { subscribe, snapshot, proxy, getVersion, ref } from 'valtio/vanilla';
import { createProxy, isChanged } from 'proxy-compare';

const subscribeKey = (proxyObject, key, callback, notifyInSync) => subscribe(proxyObject, (ops) => {
  if (ops.some((op) => op[1][0] === key)) {
    callback(proxyObject[key]);
  }
}, notifyInSync);

let currentCleanups;
const watch = (callback) => {
  const cleanups = new Set();
  const subscriptions = new Set();
  let alive = true;
  const cleanup = () => {
    cleanups.forEach((clean) => {
      clean();
    });
    cleanups.clear();
    subscriptions.clear();
  };
  const revalidate = () => {
    if (!alive) {
      return;
    }
    cleanup();
    const parent = currentCleanups;
    currentCleanups = cleanups;
    try {
      const cleanupReturn = callback((proxy) => {
        subscriptions.add(proxy);
        return proxy;
      });
      if (cleanupReturn) {
        cleanups.add(cleanupReturn);
      }
    } finally {
      currentCleanups = parent;
    }
    subscriptions.forEach((proxy) => {
      const clean = subscribe(proxy, revalidate);
      cleanups.add(clean);
    });
  };
  const wrappedCleanup = () => {
    if (alive) {
      cleanup();
      alive = false;
    }
  };
  if (currentCleanups) {
    currentCleanups.add(wrappedCleanup);
  }
  revalidate();
  return wrappedCleanup;
};

const devtools = (proxyObject, name) => {
  let extension;
  try {
    extension = window.__REDUX_DEVTOOLS_EXTENSION__;
  } catch {
  }
  if (!extension) {
    if (typeof process === "object" && process.env.NODE_ENV === "development" && typeof window !== "undefined") {
      console.warn("[Warning] Please install/enable Redux devtools extension");
    }
    return;
  }
  let isTimeTraveling = false;
  const devtools2 = extension.connect({ name });
  const unsub1 = subscribe(proxyObject, () => {
    if (isTimeTraveling) {
      isTimeTraveling = false;
    } else {
      devtools2.send(`Update - ${new Date().toLocaleString()}`, snapshot(proxyObject));
    }
  });
  const unsub2 = devtools2.subscribe((message) => {
    var _a, _b, _c, _d, _e, _f;
    if (message.type === "DISPATCH" && message.state) {
      if (((_a = message.payload) == null ? void 0 : _a.type) === "JUMP_TO_ACTION" || ((_b = message.payload) == null ? void 0 : _b.type) === "JUMP_TO_STATE") {
        isTimeTraveling = true;
      }
      const nextValue = JSON.parse(message.state);
      Object.keys(nextValue).forEach((key) => {
        proxyObject[key] = nextValue[key];
      });
    } else if (message.type === "DISPATCH" && ((_c = message.payload) == null ? void 0 : _c.type) === "COMMIT") {
      devtools2.init(snapshot(proxyObject));
    } else if (message.type === "DISPATCH" && ((_d = message.payload) == null ? void 0 : _d.type) === "IMPORT_STATE") {
      const actions = (_e = message.payload.nextLiftedState) == null ? void 0 : _e.actionsById;
      const computedStates = ((_f = message.payload.nextLiftedState) == null ? void 0 : _f.computedStates) || [];
      isTimeTraveling = true;
      computedStates.forEach(({ state }, index) => {
        const action = actions[index] || `Update - ${new Date().toLocaleString()}`;
        Object.keys(state).forEach((key) => {
          proxyObject[key] = state[key];
        });
        if (index === 0) {
          devtools2.init(snapshot(proxyObject));
        } else {
          devtools2.send(action, snapshot(proxyObject));
        }
      });
    }
  });
  devtools2.init(snapshot(proxyObject));
  return () => {
    unsub1();
    unsub2();
  };
};

const subscriptionsCache = new WeakMap();
const getSubscriptions = (proxyObject) => {
  let subscriptions = subscriptionsCache.get(proxyObject);
  if (!subscriptions) {
    subscriptions = new Map();
    subscriptionsCache.set(proxyObject, subscriptions);
  }
  return subscriptions;
};
const unstable_getDeriveSubscriptions = getSubscriptions;
const derive = (derivedFns, options) => {
  const proxyObject = (options == null ? void 0 : options.proxy) || proxy({});
  const notifyInSync = options == null ? void 0 : options.sync;
  const subscriptions = getSubscriptions(proxyObject);
  const addSubscription = (p, key, callback) => {
    const subscription = subscriptions.get(p);
    if (subscription) {
      subscription[0].set(key, callback);
    } else {
      const unsubscribe = subscribe(p, (ops) => {
        var _a;
        if (p === proxyObject && ops.every((op) => op[1].length === 1 && op[1][0] in derivedFns)) {
          return;
        }
        (_a = subscriptions.get(p)) == null ? void 0 : _a[0].forEach((cb) => {
          cb();
        });
      }, notifyInSync);
      subscriptions.set(p, [new Map([[key, callback]]), unsubscribe]);
    }
  };
  const removeSubscription = (p, key) => {
    const subscription = subscriptions.get(p);
    if (subscription) {
      const [callbackMap, unsubscribe] = subscription;
      callbackMap.delete(key);
      if (!callbackMap.size) {
        unsubscribe();
        subscriptions.delete(p);
      }
    }
  };
  Object.keys(derivedFns).forEach((key) => {
    if (Object.getOwnPropertyDescriptor(proxyObject, key)) {
      throw new Error("object property already defined");
    }
    const fn = derivedFns[key];
    let lastDependencies = null;
    const evaluate = () => {
      if (lastDependencies) {
        if (Array.from(lastDependencies).every(([p, n]) => getVersion(p) === n)) {
          return;
        }
      }
      const dependencies = new Map();
      const get = (p) => {
        dependencies.set(p, getVersion(p));
        return p;
      };
      const value = fn(get);
      const subscribe2 = () => {
        dependencies.forEach((_, p) => {
          if (!(lastDependencies == null ? void 0 : lastDependencies.has(p))) {
            addSubscription(p, key, evaluate);
          }
        });
        lastDependencies == null ? void 0 : lastDependencies.forEach((_, p) => {
          if (!dependencies.has(p)) {
            removeSubscription(p, key);
          }
        });
        lastDependencies = dependencies;
      };
      if (value instanceof Promise) {
        value.then(() => {
          subscribe2();
          evaluate();
        });
      } else {
        subscribe2();
      }
      proxyObject[key] = value;
    };
    evaluate();
  });
  return proxyObject;
};
const underive = (proxyObject, options) => {
  const subscriptions = getSubscriptions(proxyObject);
  const keysToDelete = (options == null ? void 0 : options.delete) ? new Set() : null;
  subscriptions.forEach(([callbackMap, unsubscribe], p) => {
    if (options == null ? void 0 : options.keys) {
      options.keys.forEach((key) => {
        if (callbackMap.has(key)) {
          callbackMap.delete(key);
          if (keysToDelete) {
            keysToDelete.add(key);
          }
        }
      });
    } else {
      if (keysToDelete) {
        Array.from(callbackMap.keys()).forEach((key) => {
          keysToDelete.add(key);
        });
      }
      callbackMap.clear();
    }
    if (!callbackMap.size) {
      unsubscribe();
      subscriptions.delete(p);
    }
  });
  if (keysToDelete) {
    keysToDelete.forEach((key) => {
      delete proxyObject[key];
    });
  }
};

const addComputed_DEPRECATED = (proxyObject, computedFns_FAKE, targetObject = proxyObject) => {
  console.warn("addComputed is deprecated. Please consider using `derive` or `proxyWithComputed` instead. Falling back to emulation with derive.");
  const derivedFns = {};
  Object.keys(computedFns_FAKE).forEach((key) => {
    derivedFns[key] = (get) => computedFns_FAKE[key](get(proxyObject));
  });
  return derive(derivedFns, { proxy: targetObject });
};

const proxyWithComputed = (initialObject, computedFns) => {
  Object.keys(computedFns).forEach((key) => {
    if (Object.getOwnPropertyDescriptor(initialObject, key)) {
      throw new Error("object property already defined");
    }
    const computedFn = computedFns[key];
    const { get, set } = typeof computedFn === "function" ? { get: computedFn } : computedFn;
    let computedValue;
    let prevSnapshot;
    let affected = new WeakMap();
    const desc = {};
    desc.get = () => {
      const nextSnapshot = snapshot(proxyObject);
      if (!prevSnapshot || isChanged(prevSnapshot, nextSnapshot, affected)) {
        affected = new WeakMap();
        computedValue = get(createProxy(nextSnapshot, affected));
        prevSnapshot = nextSnapshot;
      }
      return computedValue;
    };
    if (set) {
      desc.set = (newValue) => set(proxyObject, newValue);
    }
    Object.defineProperty(initialObject, key, desc);
  });
  const proxyObject = proxy(initialObject);
  return proxyObject;
};

const proxyWithHistory = (initialValue) => {
  const proxyObject = proxy({
    value: initialValue,
    history: ref({
      wip: initialValue,
      snapshots: [],
      index: -1
    }),
    canUndo: () => proxyObject.history.index > 0,
    undo: () => {
      if (proxyObject.canUndo()) {
        proxyObject.value = proxyObject.history.wip = proxyObject.history.snapshots[--proxyObject.history.index];
        proxyObject.history.snapshots[proxyObject.history.index] = snapshot(proxyObject).value;
      }
    },
    canRedo: () => proxyObject.history.index < proxyObject.history.snapshots.length - 1,
    redo: () => {
      if (proxyObject.canRedo()) {
        proxyObject.value = proxyObject.history.wip = proxyObject.history.snapshots[++proxyObject.history.index];
        proxyObject.history.snapshots[proxyObject.history.index] = snapshot(proxyObject).value;
      }
    },
    saveHistory: () => {
      proxyObject.history.snapshots.splice(proxyObject.history.index + 1);
      proxyObject.history.snapshots.push(snapshot(proxyObject).value);
      ++proxyObject.history.index;
    }
  });
  proxyObject.saveHistory();
  subscribe(proxyObject, (ops) => {
    if (ops.some((op) => op[1][0] === "value" && (op[0] !== "set" || op[2] !== proxyObject.history.wip))) {
      proxyObject.saveHistory();
    }
  });
  return proxyObject;
};

export { addComputed_DEPRECATED as addComputed, derive, devtools, proxyWithComputed, proxyWithHistory, subscribeKey, underive, unstable_getDeriveSubscriptions, watch };
